import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Users, Eye, Fingerprint, MessageSquare, Timer, Percent, ArrowUpRight, X, Monitor, Smartphone, Tablet, MapPin, LinkIcon, DoorOpen, Trash2,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell, PieChart, Pie, Legend,
} from "recharts";

const RANGES = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "All", days: 0 },
];
const AUDIENCES = [
  { label: "All", value: "all" },
  { label: "Logged-in", value: "logged_in" },
  { label: "Anonymous", value: "anon" },
];
const DEVICE_ICON = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };
const CHART_COLORS = ["#0369a1", "#059669", "#f59e0b", "#dc2626", "#7c3aed", "#0891b2", "#65a30d"];

function humanTime(seconds) {
  if (!seconds) return "0s";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export default function AnalyticsPanel() {
  const [days, setDays] = useState(30);
  const [audience, setAudience] = useState("all");
  const [includeAdmin, setIncludeAdmin] = useState(false);
  const [data, setData] = useState(null);
  const [selectedBlogId, setSelectedBlogId] = useState(null);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(() => {
    api.get(`/analytics/overview?days=${days}&audience=${audience}&include_admin=${includeAdmin}`)
       .then(r => setData(r.data));
  }, [days, audience, includeAdmin]);

  useEffect(() => { load(); }, [load]);

  const resetAll = async () => {
    if (!confirm("Delete ALL tracked analytics — every view, every engagement session, every blog view_count?\n\nThis cannot be undone.")) return;
    if (!confirm("Are you sure? Really really sure? This wipes all view history for every blog.")) return;
    setResetting(true);
    try {
      const r = await api.delete("/analytics/reset");
      toast.success(`Cleared ${r.data.views_deleted} views + ${r.data.engagements_deleted} engagements. All blog view counts reset.`);
      setData(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  if (!data) return <div className="mt-6 text-slate-500">Loading analytics…</div>;
  const { kpis, totals, daily, top_blogs, countries, devices, referrers, top_exits, pathways } = data;

  return (
    <div className="mt-6 space-y-6">
      {/* Range + audience selectors */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-slate-500">Range:</span>
          {RANGES.map(r => (
            <button key={r.label} data-testid={`analytics-range-${r.label}`}
                    onClick={() => setDays(r.days)}
                    className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded border transition-colors ${
                      days === r.days
                        ? "bg-sky-700 text-white border-sky-700"
                        : "border-slate-300 text-slate-700 hover:border-sky-700 hover:text-sky-700"
                    }`}>
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-widest text-slate-500">Audience:</span>
          {AUDIENCES.map(a => (
            <button key={a.value} data-testid={`analytics-audience-${a.value}`}
                    onClick={() => setAudience(a.value)}
                    className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded border transition-colors ${
                      audience === a.value
                        ? "bg-slate-800 text-white border-slate-800"
                        : "border-slate-300 text-slate-700 hover:border-slate-800"
                    }`}>
              {a.label}
            </button>
          ))}
          <label className="ml-3 inline-flex items-center gap-2 cursor-pointer select-none text-xs uppercase tracking-widest text-slate-500">
            <input
              type="checkbox"
              data-testid="analytics-include-admin"
              checked={includeAdmin}
              onChange={(e) => setIncludeAdmin(e.target.checked)}
              className="w-4 h-4 accent-sky-700"
            />
            Include admin views
          </label>
          <button
            data-testid="analytics-reset"
            onClick={resetAll}
            disabled={resetting}
            title="Delete all tracked analytics data"
            className="ml-2 inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3 py-1.5 rounded border border-red-300 text-red-600 hover:bg-red-600 hover:text-white disabled:opacity-40"
          >
            <Trash2 className="w-3.5 h-3.5"/> {resetting ? "Clearing…" : "Clear all data"}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Kpi icon={<Eye className="w-4 h-4"/>} label="Views" value={kpis.views.toLocaleString()}/>
        <Kpi icon={<Fingerprint className="w-4 h-4"/>} label="Unique sessions" value={kpis.unique_sessions.toLocaleString()}/>
        <Kpi icon={<Users className="w-4 h-4"/>} label="Signed-in readers" value={kpis.unique_users.toLocaleString()}/>
        <Kpi icon={<Timer className="w-4 h-4"/>} label="Avg time" value={humanTime(kpis.avg_sec)}/>
        <Kpi icon={<Percent className="w-4 h-4"/>} label="Avg scroll" value={`${kpis.avg_scroll}%`}/>
        <Kpi icon={<Percent className="w-4 h-4"/>} label="Completion" value={`${kpis.completion_rate}%`}/>
        <Kpi icon={<MessageSquare className="w-4 h-4"/>} label="Comments" value={kpis.comments}/>
      </div>

      {/* Traffic chart */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="font-heading text-lg">Traffic — views + unique sessions</CardTitle></CardHeader>
        <CardContent>
          {daily.length === 0 ? (
            <EmptyChart label="No traffic in this range yet."/>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={daily} margin={{ top: 10, right: 12, bottom: 0, left: -20 }}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false}/>
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748b" }}/>
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }}/>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
                <Legend wrapperStyle={{ fontSize: 12 }}/>
                <Line type="monotone" dataKey="views" stroke="#0369a1" strokeWidth={2} dot={false} name="Views"/>
                <Line type="monotone" dataKey="uniques" stroke="#059669" strokeWidth={2} dot={false} name="Unique sessions"/>
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Countries */}
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-700"/>Where your readers are</CardTitle></CardHeader>
          <CardContent>
            {countries.length === 0 ? (
              <EmptyChart label="No visits in this range yet."/>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={countries.slice(0, 8)} margin={{ top: 10, right: 8, bottom: 0, left: -20 }}>
                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false}/>
                    <XAxis dataKey="country" tick={{ fontSize: 11, fill: "#64748b" }}/>
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }}/>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
                    <Bar dataKey="views" fill="#0369a1" radius={[4,4,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
                <ol className="mt-4 text-sm divide-y divide-slate-100">
                  {countries.slice(0, 8).map(c => (
                    <li key={c.country} className="flex justify-between py-1">
                      <span className="text-slate-700 tabular-nums">{c.country === "XX" ? "Unknown" : c.country}</span>
                      <span className="text-slate-500 tabular-nums">{c.views} views · {c.uniques} uniques</span>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </CardContent>
        </Card>

        {/* Devices */}
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="font-heading text-lg">Devices</CardTitle></CardHeader>
          <CardContent>
            {devices.length === 0 ? (
              <EmptyChart label="No devices in this range yet."/>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={devices} dataKey="count" nameKey="device" outerRadius={80}
                         label={({ device, count }) => `${device} · ${count}`}>
                      {devices.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]}/>)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
                  </PieChart>
                </ResponsiveContainer>
                <ol className="mt-2 text-sm divide-y divide-slate-100">
                  {devices.map(d => {
                    const Icon = DEVICE_ICON[d.device] || Monitor;
                    return (
                      <li key={d.device} className="flex items-center gap-2 py-1">
                        <Icon className="w-3.5 h-3.5 text-slate-400"/>
                        <span className="text-slate-700 capitalize flex-1">{d.device}</span>
                        <span className="text-slate-500 tabular-nums">{d.count}</span>
                      </li>
                    );
                  })}
                </ol>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top blogs table */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="font-heading text-lg">Most-read blogs</CardTitle></CardHeader>
        <CardContent>
          {top_blogs.length === 0 ? (
            <EmptyChart label="No blog views in this range yet."/>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-widest text-slate-500 text-left border-b border-slate-200">
                  <th className="py-2 w-8">#</th>
                  <th>Title</th>
                  <th className="text-right">Views</th>
                  <th className="text-right">Uniques</th>
                  <th className="text-right">Avg time</th>
                  <th className="text-right">Completion</th>
                  <th className="w-8"/>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {top_blogs.map((b, i) => (
                  <tr key={b.blog_id} data-testid={`analytics-blog-row-${b.blog_id}`}
                      onClick={() => setSelectedBlogId(b.blog_id)}
                      className="hover:bg-slate-50 cursor-pointer">
                    <td className="py-2 text-slate-400 tabular-nums">{String(i+1).padStart(2, "0")}</td>
                    <td className="py-2"><span className="text-slate-900">{b.title}</span></td>
                    <td className="py-2 text-right tabular-nums">{b.views}</td>
                    <td className="py-2 text-right tabular-nums">{b.uniques}</td>
                    <td className="py-2 text-right tabular-nums">{humanTime(b.avg_sec)}</td>
                    <td className="py-2 text-right tabular-nums">{b.completion_rate}%</td>
                    <td className="text-right"><ArrowUpRight className="w-3.5 h-3.5 text-slate-400"/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Referrers */}
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><LinkIcon className="w-4 h-4 text-sky-700"/>Top referrers</CardTitle></CardHeader>
          <CardContent>
            {referrers.length === 0 ? <EmptyChart label="No referral data yet."/> : (
              <ol className="text-sm divide-y divide-slate-100">
                {referrers.map(r => (
                  <li key={r.source} className="flex justify-between py-2">
                    <span className="text-slate-700">{r.source === "direct" ? "Direct / bookmarks" : r.source}</span>
                    <span className="text-slate-500 tabular-nums">{r.count}</span>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>

        {/* Top exits */}
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><DoorOpen className="w-4 h-4 text-sky-700"/>Top exit blogs</CardTitle></CardHeader>
          <CardContent>
            {top_exits.length === 0 ? <EmptyChart label="No exits recorded yet."/> : (
              <ol className="text-sm divide-y divide-slate-100">
                {top_exits.map(e => (
                  <li key={e.blog_id} className="flex justify-between py-2">
                    <span className="text-slate-700 flex-1 truncate mr-2">{e.title}</span>
                    <span className="text-slate-500 tabular-nums">{e.exits} exits</span>
                  </li>
                ))}
              </ol>
            )}
            <p className="mt-3 text-xs text-slate-400">These are the blogs readers were on when they closed the tab or navigated away — often the last chapter of a good pathway, sometimes a friction point.</p>
          </CardContent>
        </Card>
      </div>

      {/* Pathways */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="font-heading text-lg">Pathways</CardTitle></CardHeader>
        <CardContent>
          {pathways.length === 0 ? <EmptyChart label="No pathways or pathway visits yet."/> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-widest text-slate-500 text-left border-b border-slate-200">
                  <th className="py-2">Pathway</th>
                  <th className="text-right">Blogs</th>
                  <th className="text-right">Sessions touched</th>
                  <th className="text-right">Fully completed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pathways.map(p => (
                  <tr key={p.pathway_id}>
                    <td className="py-2 text-slate-900">{p.title}</td>
                    <td className="py-2 text-right tabular-nums">{p.total_blogs}</td>
                    <td className="py-2 text-right tabular-nums">{p.sessions}</td>
                    <td className="py-2 text-right tabular-nums">
                      {p.completions} <span className="text-slate-400">({p.sessions ? Math.round(100 * p.completions / p.sessions) : 0}%)</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Content totals footer */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {Object.entries(totals).map(([k, v]) => (
          <div key={k} className="border border-slate-200 rounded-lg p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{k}</div>
            <div className="font-heading text-lg font-semibold text-slate-900 tabular-nums">{v}</div>
          </div>
        ))}
      </div>

      {/* Blog detail modal */}
      {selectedBlogId && (
        <BlogDetailModal blogId={selectedBlogId} days={days} audience={audience} includeAdmin={includeAdmin} onClose={() => setSelectedBlogId(null)}/>
      )}
    </div>
  );
}

function Kpi({ icon, label, value }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500">
        <span className="text-sky-700">{icon}</span>{label}
      </div>
      <div className="font-heading text-2xl font-semibold text-slate-900 mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function EmptyChart({ label }) {
  return <div className="text-sm text-slate-400 py-8 text-center border border-dashed border-slate-200 rounded">{label}</div>;
}

function BlogDetailModal({ blogId, days, audience, includeAdmin, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/analytics/blog/${blogId}?days=${days}&audience=${audience}&include_admin=${includeAdmin}`)
       .then(r => setData(r.data));
  }, [blogId, days, audience, includeAdmin]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-auto p-6" onClick={e=>e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-sky-700 mb-1">Blog analytics</div>
            <h3 className="font-heading text-xl font-semibold text-slate-900">
              {data?.blog?.title || "Loading…"}
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5"/></button>
        </div>
        {!data ? <div className="text-slate-500 py-8">Loading…</div> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <Kpi icon={<Eye className="w-4 h-4"/>} label="Views" value={data.kpis.views}/>
              <Kpi icon={<Fingerprint className="w-4 h-4"/>} label="Uniques" value={data.kpis.unique_sessions}/>
              <Kpi icon={<Timer className="w-4 h-4"/>} label="Avg time" value={humanTime(data.kpis.avg_sec)}/>
              <Kpi icon={<Percent className="w-4 h-4"/>} label="Avg scroll" value={`${data.kpis.avg_scroll}%`}/>
              <Kpi icon={<Percent className="w-4 h-4"/>} label="Completion" value={`${data.kpis.completion_rate}%`}/>
            </div>
            {data.daily.length > 0 && (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data.daily} margin={{ top: 10, right: 12, bottom: 0, left: -20 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false}/>
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748b" }}/>
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }}/>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
                  <Line type="monotone" dataKey="views" stroke="#0369a1" strokeWidth={2} dot={false} name="Views"/>
                </LineChart>
              </ResponsiveContainer>
            )}
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <MiniList title="Countries" items={data.countries.map(c => ({ label: c.country === "XX" ? "Unknown" : c.country, value: c.count }))}/>
              <MiniList title="Devices" items={data.devices.map(d => ({ label: d.device, value: d.count }))}/>
              <MiniList title="Referrers" items={data.referrers.map(r => ({ label: r.source === "direct" ? "Direct / bookmarks" : r.source, value: r.count }))}/>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MiniList({ title, items }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{title}</div>
      {items.length === 0 ? <div className="text-xs text-slate-400">No data.</div> : (
        <ol className="text-sm divide-y divide-slate-100">
          {items.map((it, i) => (
            <li key={i} className="flex justify-between py-1">
              <span className="text-slate-700 capitalize">{it.label}</span>
              <span className="text-slate-500 tabular-nums">{it.value}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
